import React, { useEffect, useCallback, useState } from 'react';

import PropTypes from 'prop-types';

import { setExternalPreference } from '@zr/utilities/api/preference';
import logScreenView from '@zr/utilities/logScreenView';

import './MobileAppPromo.less';

import {
  STRING_DESKTOP_TITLE,
  STRING_MOBILE_TITLE,
  STRING_NOTIFICATION_PROMO_DESKTOP_DESCRIPTION,
  STRING_NOTIFICATION_PROMO_QR_CODE_DESCRIPTION,
  STRING_PRODUCT_PROMO_DESKTOP_DESCRIPTION,
  STRING_PRODUCT_PROMO_QR_CODE_DESCRIPTION,
  STRING_NOTIFICATION_PROMO_MOBILE_DESCRIPTION,
  STRING_PRODUCT_PROMO_MOBILE_DESCRIPTION,
  STRING_PROMO_BUTTON,
  STRING_PROMO_BUTTON_MOBILE,
  STRING_PRODUCT_DOWNLOAD_URL,
  STRING_NOTIFICATION_DOWNLOAD_URL,
  STRING_PRODUCT_QR_CODE_FILEPATH,
  STRING_NOTIFICATION_QR_CODE_FILEPATH,
  STRING_MOBILE_NOTIFICATION_IMAGE_FILEPATH,
  STRING_MOBILE_PRODUCT_IMAGE_FILEPATH,
} from './labels';

export function MobileAppPromo({
  containerElementSelector,
  logScreenViewArea,
  onContinue,
  useDesktopContainerStyles,
  useResponsiveMobileButton,
  screenType,
}) {
  const [width, setWindowWidth] = useState(0);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [qrCodeDescription, setQrCodeDescription] = useState('');

  const SCREEN_TYPE = screenType;
  const isDesktop = width > 767;
  const isNotificationBucket = screenType === 'ii_notification_promo';
  const isProductBucket = screenType === 'ii_product_promo';
  const qrCodeUrl = isNotificationBucket ? STRING_NOTIFICATION_QR_CODE_FILEPATH : STRING_PRODUCT_QR_CODE_FILEPATH;
  const mobileDownloadUrl = isNotificationBucket ? STRING_NOTIFICATION_DOWNLOAD_URL : STRING_PRODUCT_DOWNLOAD_URL;

  const handleDownloadRedirect = (e) => {
    e.preventDefault();
    window.location.href = mobileDownloadUrl;
  };

  const updateDimensions = () => {
    const windowWidth = window.innerWidth;
    setWindowWidth(windowWidth);
  };

  const handleClick = () => {
    if (logScreenViewArea) {
      logScreenView(logScreenViewArea, SCREEN_TYPE, {
        screen_step_name: 'completed',
      });
    }
    onContinue();
  };

  useEffect(() => {
    if (logScreenViewArea) {
      logScreenView(logScreenViewArea, SCREEN_TYPE);
    }
  }, [SCREEN_TYPE, logScreenViewArea]);

  // preference that prevents seeing this again
  useEffect(() => {
    setExternalPreference('contact', 'seen_mobile_app_promo', 'yes');
  }, []);

  useEffect(() => {
    updateDimensions();
    if (isDesktop && isNotificationBucket) {
      setTitle(STRING_DESKTOP_TITLE);
      setDescription(STRING_NOTIFICATION_PROMO_DESKTOP_DESCRIPTION);
      setImageUrl(STRING_MOBILE_NOTIFICATION_IMAGE_FILEPATH);
      setQrCodeDescription(STRING_NOTIFICATION_PROMO_QR_CODE_DESCRIPTION);
    } else if (isDesktop && isProductBucket) {
      setTitle(STRING_DESKTOP_TITLE);
      setDescription(STRING_PRODUCT_PROMO_DESKTOP_DESCRIPTION);
      setImageUrl(STRING_MOBILE_PRODUCT_IMAGE_FILEPATH);
      setQrCodeDescription(STRING_PRODUCT_PROMO_QR_CODE_DESCRIPTION);
    } else if (!isDesktop && isNotificationBucket) {
      setTitle(STRING_MOBILE_TITLE);
      setDescription(STRING_NOTIFICATION_PROMO_MOBILE_DESCRIPTION);
      setImageUrl(STRING_MOBILE_NOTIFICATION_IMAGE_FILEPATH);
    } else {
      setTitle(STRING_MOBILE_TITLE);
      setDescription(STRING_PRODUCT_PROMO_MOBILE_DESCRIPTION);
      setImageUrl(STRING_MOBILE_PRODUCT_IMAGE_FILEPATH);
    }
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [isDesktop, isNotificationBucket, isProductBucket]);

  // if container specified, use this element for scrolling calculations
  const queryContainer = useCallback(() => (
    containerElementSelector ? document.querySelector(containerElementSelector) : null
  ), [containerElementSelector]);

  // checks when they've scrolled to the bottom of this content
  const isScrolledToBottom = useCallback(() => {
    const content = document.querySelector('.MobileAppPromo');
    let scrollBottom;
    // get how far they've scrolled down
    // normally just check window, but in something like a modal check the scroll value in the element instead
    const container = queryContainer();
    if (container) {
      scrollBottom = container.scrollTop + window.innerHeight;
    } else {
      scrollBottom = window.scrollY + window.innerHeight;
    }
    // bottom position of eligibility celebration content
    const containerBottom = content.offsetTop + content.scrollHeight;
    return scrollBottom >= containerBottom;
  }, [queryContainer]);

  const [fixMobileButton, setFixMobileButton] = useState(false);

  // fix the mobile button until they reach the bottom (presumably to show the legal footer below this celebration)
  const setMobileStyle = useCallback(() => {
    setFixMobileButton(!isScrolledToBottom());
  }, [isScrolledToBottom]);

  useEffect(() => {
    let scroller;
    // only do this fancy responsive animation if prop is set
    if (useResponsiveMobileButton) {
      // set the fixed/not fixed styling initially, and on scroll events
      setMobileStyle();
      // set the scroll listener to the container
      // usually it's window that's scrolling, but in something like a modal add the listener to that element instead
      const container = queryContainer();
      scroller = container || window;
      scroller.addEventListener('scroll', setMobileStyle);
    }
    return () => {
      scroller?.removeEventListener('scroll', setMobileStyle);
    };
  }, [queryContainer, setMobileStyle, useResponsiveMobileButton]);

  return (
    <div className={`MobileAppPromo ${useDesktopContainerStyles ? 'desktop_container' : ''}`}>
      <div className="content_container">
        <div className="promo_header">
          <div className="promo_title">
            {title}
          </div>
          <div className="promo_description">
            {description}
          </div>
        </div>
        <div className={isNotificationBucket ? 'promo_images_notification' : 'promo_images_product'}>
          <div className="promo_wrapper">
            <img src={imageUrl} className={isNotificationBucket ? 'notification_promo' : 'product_promo'} alt="" />
          </div>
          {isDesktop && (
            <div className={isNotificationBucket ? 'qr_code_wrapper_notification' : 'qr_code_wrapper_product'}>
              <img src={qrCodeUrl} className="qr_code" alt="" />
              <div className={isNotificationBucket ? 'qr_code_description_notification' : 'qr_code_description_product'}>
                {qrCodeDescription}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={`button_container ${fixMobileButton ? 'fix_mobile_button' : 'scroll_mobile_button'}`}>
        <button
          className={`continue_button ${fixMobileButton ? 'fix_mobile_button' : 'scroll_mobile_button'}`}
          onClick={isDesktop ? handleClick : (e) => handleDownloadRedirect(e)}
          type="button"
        >
          {isDesktop ? STRING_PROMO_BUTTON : STRING_PROMO_BUTTON_MOBILE }
        </button>
      </div>
    </div>
  );
}

MobileAppPromo.propTypes = {
  containerElementSelector: PropTypes.string,
  logScreenViewArea: PropTypes.string,
  onContinue: PropTypes.func.isRequired,
  useDesktopContainerStyles: PropTypes.bool,
  useResponsiveMobileButton: PropTypes.bool,
  screenType: PropTypes.string,
};

MobileAppPromo.defaultProps = {
  containerElementSelector: null,
  logScreenViewArea: null,
  useDesktopContainerStyles: false,
  useResponsiveMobileButton: false,
  screenType: '',
};

export default MobileAppPromo;
